<script setup>
import '@campact/camper/core/CmprButton';
import '@campact/camper/icons/CmprIconSolidLock';
import CmprCheckbox from '@cta/frontend/components/CmprCheckbox.vue';

import { ref, onMounted, provide, computed, useTemplateRef } from 'vue';

import { debounce } from 'lodash/function';

import csrfToken from '@cta/shared/csrf';
import FriendlyCaptcha from '@cta/frontend/components/FriendlyCaptcha.vue';
import TlsNotice from '@cta/donate_call_component/components/TlsNotice.vue';
import initializeStore from './utils/store';
import AddressStep from './components/AddressStep.vue';
import AnswerStep from './components/AnswerStep.vue';
import scrollIntoView from '@cta/frontend/javascripts/scroll_into_view';
import browserService from '@cta/frontend/services/BrowserService';
import trackingService from '@cta/frontend/services/TrackingService';

const props = defineProps({
  answerOptions: {
    type: Array,
    default() {
      return [];
    },
  },
  answerAllowMultiple: {
    type: Boolean,
    default: false,
  },
  answerTitles: {
    type: Object,
    default() {
      return {
        default: undefined,
        valid: undefined,
      };
    },
  },
  salutations: {
    type: Array,
    default() {
      return [];
    },
  },
  defaultSalutation: {
    type: String,
    default: undefined,
  },
  countryCodes: {
    type: Object,
    default() {
      return {};
    },
  },
  defaultCountryCode: {
    type: String,
    default: undefined,
  },
  regions: {
    type: Array,
    default() {
      return [];
    },
  },
  prefilledAttributes: {
    type: Object,
    default() {
      return {
        salutation: undefined,
        firstname: undefined,
        lastname: undefined,
        countrycode: undefined,
        postalcode: undefined,
        region: undefined,
        locality: undefined,
        street: undefined,
        emailaddress: undefined,
      };
    },
  },
  validatePath: {
    type: String,
    default: undefined,
  },
  answerPath: {
    type: String,
    default: undefined,
  },
  logoutPath: {
    type: String,
    default: undefined,
  },
  utmParameters: {
    type: Object,
    default: undefined,
  },
  collectAddress: {
    type: Boolean,
    default: false,
  },
  collectMobileNumber: {
    type: Boolean,
    default: false,
  },
  mobileNumberDisclaimer: {
    type: String,
    default: undefined,
  },
  mobileNumberRequired: {
    type: Boolean,
    default: false,
  },
  explicitConsentRequired: {
    type: Boolean,
    default: false,
  },
  explicitConsentText: {
    type: String,
    default: undefined,
  },
  exitUrl: {
    type: String,
    default: undefined,
  },
  submitButtonText: {
    type: String,
    default: '',
  },
  i18n: {
    type: Object,
    default: undefined,
  },
  signedIn: {
    type: Boolean,
    default: false,
  },
  friendlyCaptchaSettings: {
    type: Object,
    default() {
      return {
        showCaptcha: false,
        simulateCaptchaMode: 'off',
        apiEndpoint: undefined,
        sitekey: undefined,
      };
    },
  },
});

const store = initializeStore({
  collectAddress: props.collectAddress,
  defaultCountryCode: props.defaultCountryCode,
  defaultSalutation: props.defaultSalutation,
  prefilledAttributes: props.prefilledAttributes,
});
provide('store', store);

const form = useTemplateRef('form');
const answerStep = useTemplateRef('answer-step');
const addressStep = useTemplateRef('address-step');

function openAnswerStep() {
  store.openStep = 'answer';
  scrollIntoView(answerStep.value.$el);
}

function openAddressStep() {
  store.openStep = 'address';
  scrollIntoView(addressStep.value.$el);
}

function closeAllSteps() {
  store.openStep = undefined;
}

function allStepsClosed() {
  return store.openStep === undefined;
}

function openFirstInvalidStep() {
  if (!store.answerStepValid) {
    openAnswerStep();
  } else if (!store.addressStepValid) {
    openAddressStep();
  } else {
    closeAllSteps();
  }
}

function showExplicitConsentError() {
  return store.explicitConsentErrorUnlocked && store.errors.explicitConsent && store.values.explicitConsent === false;
}

function showExplicitConsent() {
  return props.explicitConsentText !== undefined;
}

function createFormData() {
  const formData = new FormData();
  const answers = (typeof store.values.answers === 'object') ? store.values.answers : [store.values.answers];
  answers.forEach((answer) => formData.append('form_form[answers][]', answer));
  formData.append('form_form[salutation]', store.values.salutation || '');
  formData.append('form_form[firstname]', store.values.firstname || '');
  formData.append('form_form[lastname]', store.values.lastname || '');
  formData.append('form_form[street]', store.values.street || '');
  formData.append('form_form[countrycode]', store.values.countrycode || '');
  formData.append('form_form[postalcode]', store.values.postalcode || '');
  formData.append('form_form[region]', store.values.region || '');
  formData.append('form_form[locality]', store.values.locality || '');
  formData.append('form_form[emailaddress]', store.values.emailaddress || '');
  formData.append('form_form[mobile_number]', store.values.mobile_number || '');
  formData.append('form_form[explicit_consent]', store.values.explicitConsent ? props.explicitConsentText : '');
  formData.append('form_form[opt_out_account_update]', store.values.optOutAccountUpdate || '');
  formData.append('form_form[captcha_solution]', captchaSolution.value || '');

  return formData;
}

async function fetchValidation(path) {
  const response = await fetch(path, {
    method: 'post',
    body: createFormData(),
    headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken() },
  });
  const json = await response.json();

  return { ok: response?.ok, messages: new Map(Object.entries(json)) };
}

async function validate() {
  const { ok, messages } = await fetchValidation(props.validatePath);
  if (ok) {
    store.errors = {
      answers: messages.get('answers'),
      emailaddress: messages.get('emailaddress'),
      explicitConsent: messages.get('explicit_consent'),
      firstname: messages.get('firstname'),
      lastname: messages.get('lastname'),
      locality: messages.get('locality'),
      mobile_number: messages.get('mobile_number'),
      postalcode: messages.get('postalcode'),
      region: messages.get('region'),
      street: messages.get('street'),
    };
  } else {
    browserService.reload();
  }
}

const throttledValidate = debounce(async () => validate(), 500, { leading: true });

async function isAnswerStepValid() {
  await validate();
  return store.answerStepValid;
}

async function tryOpenAnswerStep() {
  if (allStepsClosed()) {
    openAnswerStep();
  } else {
    await validate();
    openAnswerStep();
  }
}

async function commitAnswer(navigate = openFirstInvalidStep) {
  if (await isAnswerStepValid()) {
    trackingService.dispatchSendForm(answerStep.value.$el, {
      context: 'answer-step',
    });
    navigate();
  } else {
    store.answerErrorUnlocked = true;
  }
}

async function isAddressStepValid() {
  await validate();
  return store.addressStepValid;
}

async function tryOpenAddressStep() {
  if (allStepsClosed()) {
    openAddressStep();
  } else if (store.answerStepOpen) {
    await commitAnswer(openAddressStep);
  } else {
    openAddressStep();
  }
}

async function commitAddress(navigate = openFirstInvalidStep) {
  if (await isAddressStepValid()) {
    trackingService.dispatchSendForm(answerStep.value.$el, {
      context: 'address-step',
    });
    navigate();
  } else {
    store.addressErrorUnlocked = true;
  }
}

var submitButtonActive = false;
const submitPending = ref(false);
const captchaError = ref(undefined);
const captchaSolution = ref(!props.friendlyCaptchaSettings.showCaptcha
  || props.friendlyCaptchaSettings.simulateCaptchaMode === 'auto'
  ? 'simulated-response'
  : undefined);
const captchaValid = computed(() => captchaError.value === undefined);

const captchaComplete = () => {
  captchaError.value = undefined;

  if (!submitButtonActive) {
    trySubmit();
  }
};

async function submit(path) {
  const formData = createFormData();

  Object.entries(props.utmParameters).forEach(([key, value]) => {
    if (value) formData.append(key, value);
  });

  const response = await fetch(path, {
    method: 'post',
    body: formData,
    headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken() },
  });

  if (response.ok) {
    const json = await response.json();
    const messages = new Map(Object.entries(json));
    captchaError.value = messages.get('captcha_solution');

    if (captchaValid.value) {
      trackingService.dispatchSendForm(form.value, {
        context: 'order-form',
      });
      browserService.redirectDelayed(json.redirectUrl);
    }
  } else {
    browserService.reload();
  }
}

async function submitClicked() {
  submitButtonActive = true;
  submitPending.value = true;

  if (store.answerStepOpen) await commitAnswer();
  if (store.addressStepOpen) await commitAddress();
  if (!store.explicitConsentValid) {
    await validate();
    store.explicitConsentErrorUnlocked = !store.explicitConsentValid;
  }

  trySubmit();
  submitButtonActive = false;
}

async function trySubmit() {
  if (captchaSolution.value) {
    if (store.answerStepValid && store.addressStepValid && store.explicitConsentValid && submitPending.value) {
      await submit(props.answerPath);

      store.captchaErrorUnlocked = !store.captchaValid;
    }

    setTimeout(() => {
      submitPending.value = false;
    }, 1000);
  }
}

onMounted(async () => {
  if (store.addressPrefilled) {
    await validate();

    store.addressErrorUnlocked = !store.addressStepValid;
  }
});
</script>

<template>
  <form
    ref="form"
    data-analytics-id="form-call-form"
  >
    <AnswerStep
      ref="answer-step"
      :answer-allow-multiple="answerAllowMultiple"
      :answer-options="answerOptions"
      :answer-titles="answerTitles"
      :i18n="i18n"
      @commit="commitAnswer"
      @try-open="tryOpenAnswerStep"
    />
    <AddressStep
      ref="address-step"
      :collect-address="collectAddress"
      :collect-mobile-number="collectMobileNumber"
      :country-codes="countryCodes"
      :i18n="i18n"
      :logout-path="logoutPath"
      :mobile-number-disclaimer="mobileNumberDisclaimer"
      :mobile-number-required="mobileNumberRequired"
      :prefilled-attributes="prefilledAttributes"
      :regions="regions"
      :salutations="salutations"
      :signed-in="signedIn"
      @commit="commitAddress"
      @try-open="tryOpenAddressStep"
      @validate="throttledValidate"
    />

    <div
      v-if="showExplicitConsent()"
      class="cmpr-checkbox-control mb-90"
      data-size="small"
    >
      <CmprCheckbox
        id="explicit-consent-checkbox"
        v-model="store.values.explicitConsent"
        class="cmpr-checkbox-control__input"
        data-analytics-context="order-form"
        data-analytics-id="form-call-explicit-consent-checkbox"
        data-analytics-inner-text="explicit-consent"
      />
      <label
        class="cmpr-checkbox-control__label"
        for="explicit-consent-checkbox"
      >
        {{ explicitConsentText }}
      </label>

      <cmpr-form-hint
        v-if="showExplicitConsentError()"
        variant="error"
      >
        {{ store.errors.explicitConsent }}
      </cmpr-form-hint>
    </div>

    <FriendlyCaptcha
      v-model:solution="captchaSolution"
      :error="captchaError"
      :error-unlocked="!captchaValid"
      :settings="friendlyCaptchaSettings"
      @complete="captchaComplete"
    >
      <cmpr-button
        class="w-100 mb-50 mt-50"
        data-analytics-context="order-form"
        data-analytics-id="form-call-form-submit-button"
        :disabled="submitPending"
        size="large"
        variant="primary"
        @click.prevent="submitClicked"
      >
        <cmpr-icon-solid-lock />
        {{ submitButtonText }}
      </cmpr-button>

      <tls-notice>
        {{ i18n.tlsNotice }}
      </tls-notice>
    </FriendlyCaptcha>
  </form>
</template>
